import React from 'react';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { List, Info } from 'lucide-react';
import SelecteurActivites from '../../components/SelecteurActivites';
import { InlineError } from '../../components/ui/FormField';

export default function ActivitesSelectionSection({
  selectedActivitesGO, setSelectedActivitesGO,
  selectedActivites2nd, setSelectedActivites2nd,
  selectedActivitesPIB, setSelectedActivitesPIB,
  selectedActivitesTP, setSelectedActivitesTP,
  errors, clearError, handleActivitesLoaded, activitesCouvertes = {}
}) {
  const couv = (k) => (Array.isArray(activitesCouvertes[k]) ? activitesCouvertes[k] : []);
  const nbCouvertes = Object.values(activitesCouvertes).reduce((n, l) => n + (Array.isArray(l) ? l.length : 0), 0);
  return (
              <div className="space-y-6" data-testid="activites-section">
                <div className="flex items-center gap-2">
                  <List className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-sm">Sélection des activités <span className="text-red-500">*</span></h3>
                </div>
                
                {/* Erreur inline pour les activités */}
                {errors.activites && <InlineError message={errors.activites} />}
                {nbCouvertes > 0 && (
                  <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="activites-couvertes-info">
                    {nbCouvertes} activité(s) déjà couverte(s) par le contrat parent apparaissent grisées : sélectionnez uniquement les nouvelles activités de l&apos;avenant.
                  </p>
                )}
                
                {/* Section Activités BTP (Gros Œuvre, Second Œuvre, Travaux Publics) */}
                <div className={`mb-6 ${errors.activites ? 'ring-2 ring-red-200 rounded-lg p-2' : ''}`}>
                  <div className={`mb-3 p-3 rounded-lg ${
                    selectedActivitesPIB.length > 0 
                      ? 'bg-amber-50 border-2 border-amber-300'
                      : 'bg-blue-50'
                  }`}>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                      Activités BTP (Gros Œuvre, Second Œuvre, Travaux Publics)
                    </h4>
                    {selectedActivitesPIB.length > 0 && (
                      <div className="flex items-start gap-2 mt-2 p-2 bg-amber-100 rounded-lg">
                        <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-800">
                          <span className="font-semibold">Information :</span> Les activités BTP et PIB relèvent de garanties distinctes et ne peuvent être combinées sur une même souscription. Pour accéder aux activités BTP, veuillez d'abord désélectionner les activités PIB.
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {selectedActivitesPIB.length > 0 ? (
                    <div className="bg-slate-100 rounded-lg p-4 text-center text-slate-500">
                      <p className="text-sm">Section BTP désactivée - Désélectionnez les activités PIB pour y accéder</p>
                    </div>
                  ) : (
                    <Tabs defaultValue="gros_oeuvre" className="w-full">
                      <TabsList className="grid w-full grid-cols-3 bg-slate-100">
                        <TabsTrigger value="gros_oeuvre" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                          Gros Œuvre
                          {selectedActivitesGO.length > 0 && (
                            <Badge className="bg-blue-800 text-white ml-2 font-semibold">{selectedActivitesGO.length}</Badge>
                          )}
                        </TabsTrigger>
                        <TabsTrigger value="second_oeuvre" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
                          Second Œuvre
                          {selectedActivites2nd.length > 0 && (
                            <Badge className="bg-green-800 text-white ml-2 font-semibold">{selectedActivites2nd.length}</Badge>
                          )}
                        </TabsTrigger>
                        <TabsTrigger value="travaux_publics" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
                          Travaux Publics
                          {selectedActivitesTP.length > 0 && (
                            <Badge className="bg-orange-800 text-white ml-2 font-semibold">{selectedActivitesTP.length}</Badge>
                          )}
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="gros_oeuvre" className="mt-4">
                        <SelecteurActivites
                          categorie="gros_oeuvre"
                          coveredIds={couv('gros_oeuvre')}
                          selectedIds={selectedActivitesGO}
                          onChange={(ids) => {
                            setSelectedActivitesGO(ids);
                            if (errors.activites) clearError('activites');
                          }}
                          onActivitesLoaded={handleActivitesLoaded}
                        />
                      </TabsContent>

                      <TabsContent value="second_oeuvre" className="mt-4">
                        <SelecteurActivites
                          categorie="second_oeuvre"
                          coveredIds={couv('second_oeuvre')}
                          selectedIds={selectedActivites2nd}
                          onChange={(ids) => {
                            setSelectedActivites2nd(ids);
                            if (errors.activites) clearError('activites');
                          }}
                          onActivitesLoaded={handleActivitesLoaded}
                        />
                      </TabsContent>

                      <TabsContent value="travaux_publics" className="mt-4">
                        <SelecteurActivites
                          categorie="travaux_publics"
                          coveredIds={couv('travaux_publics')}
                          selectedIds={selectedActivitesTP}
                          onChange={(ids) => {
                            setSelectedActivitesTP(ids);
                            if (errors.activites) clearError('activites');
                          }}
                          onActivitesLoaded={handleActivitesLoaded}
                        />
                      </TabsContent>
                    </Tabs>
                  )}
                </div>

                {/* Section PIB - MUTUELLEMENT EXCLUSIF avec les activités BTP */}
                <div className={`border-t-2 border-dashed border-slate-300 pt-6 mt-6 ${errors.activites ? 'ring-2 ring-red-200 rounded-lg p-2' : ''}`}>
                  <div className={`rounded-lg p-4 mb-4 ${
                    (selectedActivitesGO.length > 0 || selectedActivites2nd.length > 0 || selectedActivitesTP.length > 0) 
                      ? 'bg-amber-50 border-2 border-amber-300' 
                      : 'bg-gradient-to-r from-purple-50 to-indigo-50'
                  }`}>
                    <h4 className="text-sm font-semibold text-purple-800 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-purple-600"></span>
                      Prestations Intellectuelles du Bâtiment (PIB)
                    </h4>
                    {(selectedActivitesGO.length > 0 || selectedActivites2nd.length > 0 || selectedActivitesTP.length > 0) ? (
                      <div className="flex items-start gap-2 mt-2 p-2 bg-amber-100 rounded-lg">
                        <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-800">
                          <span className="font-semibold">Information :</span> Les activités PIB et BTP relèvent de garanties distinctes et ne peuvent être combinées sur une même souscription. Pour accéder aux activités PIB, veuillez d'abord désélectionner les activités BTP (Gros Œuvre, Second Œuvre, Travaux Publics).
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-purple-600 italic flex items-center gap-1.5">
                        <Info className="h-3.5 w-3.5" />
                        Les activités PIB et BTP relèvent de garanties distinctes et ne peuvent être combinées.
                      </p>
                    )}
                  </div>
                  {(selectedActivitesGO.length > 0 || selectedActivites2nd.length > 0 || selectedActivitesTP.length > 0) ? (
                    <div className="bg-slate-100 rounded-lg p-4 text-center text-slate-500">
                      <p className="text-sm">Section PIB désactivée - Désélectionnez les activités BTP pour y accéder</p>
                    </div>
                  ) : (
                    <>
                      <SelecteurActivites
                        categorie="intellectuels"
                        coveredIds={[...couv('intellectuels'), ...couv('pib')]}
                        selectedIds={selectedActivitesPIB}
                        onChange={(ids) => {
                          setSelectedActivitesPIB(ids);
                          if (errors.activites) clearError('activites');
                        }}
                        onActivitesLoaded={(cat, acts) => handleActivitesLoaded('pib', acts)}
                      />
                      {selectedActivitesPIB.length > 0 && (
                        <div className="mt-3 flex items-center gap-2">
                          <Badge className="bg-purple-600 text-white font-semibold">
                            {selectedActivitesPIB.length} activité(s) PIB sélectionnée(s)
                          </Badge>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
  );
}
